import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import EventDetail from '../components/EventDetail';
import AddEventModal from '../components/AddEventModal';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import API_BASE from '../config/api';

const STATUS_COLORS = {
  'Proposal':  { bg: 'rgba(229,192,75,0.15)',  color: '#E5C04B' },
  'Confirmed': { bg: 'rgba(83,145,255,0.15)',  color: '#5391FF' },
  'Active':    { bg: 'rgba(57,255,20,0.15)',   color: '#39FF14' },
  'Completed': { bg: 'rgba(86,86,86,0.2)',     color: '#565656' },
  'Archived':  { bg: 'rgba(40,40,40,0.8)',     color: '#3a3a3a' },
};

function formatDateRange(start, end) {
  if (!start && !end) return '';
  const opts = { month: 'short', day: 'numeric' };
  const yearOpts = { month: 'short', day: 'numeric', year: 'numeric' };
  const fmt = (d, o) => new Date(d).toLocaleDateString('en-US', o);
  if (start && end) {
    const s = new Date(start);
    const e = new Date(end);
    if (s.getFullYear() === e.getFullYear()) {
      return `${fmt(s, opts)} — ${fmt(e, yearOpts)}`;
    }
    return `${fmt(s, yearOpts)} — ${fmt(e, yearOpts)}`;
  }
  if (start) return fmt(start, yearOpts);
  return fmt(end, yearOpts);
}

function EventCard({ event, onClick }) {
  const [hovered, setHovered] = useState(false);
  const sc = STATUS_COLORS[event.status] || STATUS_COLORS['Proposal'];
  const dateStr = formatDateRange(event.start_date, event.end_date);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#2a2a2a',
        border: `1px solid ${hovered ? 'rgba(57,255,20,0.3)' : '#3a3a3a'}`,
        borderRadius: '8px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.3)' : 'none',
      }}
    >
      {/* Status badge */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <span style={{
          backgroundColor: sc.bg,
          color: sc.color,
          border: `1px solid ${sc.color}`,
          borderRadius: '8px',
          padding: '2px 8px',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '10px',
          whiteSpace: 'nowrap',
        }}>
          {event.status || 'Proposal'}
        </span>
      </div>

      {/* Title */}
      <div style={{
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 700,
        fontSize: '18px',
        color: '#FFFFFF',
        marginBottom: '6px',
        lineHeight: 1.3,
      }}>
        {event.title}
      </div>

      {/* Project name */}
      <div style={{
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: '13px',
        color: event.project_title ? '#39FF14' : '#565656',
        marginBottom: '8px',
      }}>
        {event.project_title || 'No Project'}
      </div>

      {/* Location */}
      {event.location && (
        <div style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 400,
          fontSize: '13px',
          color: '#888888',
          marginBottom: '4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {event.location}
        </div>
      )}

      {/* Date range */}
      {dateStr && (
        <div style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 400,
          fontSize: '12px',
          color: '#565656',
        }}>
          {dateStr}
        </div>
      )}
    </div>
  );
}

function BackButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'transparent',
        border: 'none',
        color: hovered ? '#F5F5F5' : '#565656',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        cursor: 'pointer',
        padding: '0',
        transition: 'color 0.15s ease',
      }}
    >
      <ArrowBackOutlined style={{ fontSize: 16, color: 'inherit' }} />
      All Events
    </button>
  );
}

export default function Events() {
  const { token } = useAuth();
  const { activeWorkspace, addRecentEvent } = useWorkspace();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalProjectId, setAddModalProjectId] = useState(null);
  const [addModalProjectName, setAddModalProjectName] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!activeWorkspace) { setIsLoading(false); return; }
    setIsLoading(true);
    axios
      .get(`${API_BASE}/api/events`, {
        params: { workspace_id: activeWorkspace._id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setEvents(res.data);
        setIsLoading(false);

        const selectId = sessionStorage.getItem('vance_select_event');
        if (selectId) {
          const found = res.data.find((e) => e._id === selectId);
          if (found) {
            setSelectedEvent(found);
            addRecentEvent(found);
          }
          sessionStorage.removeItem('vance_select_event');
        }

        const newEventProjectId = sessionStorage.getItem('vance_new_event_project_id');
        const newEventProjectName = sessionStorage.getItem('vance_new_event_project_name');
        if (newEventProjectId) {
          setAddModalProjectId(newEventProjectId);
          setAddModalProjectName(newEventProjectName || '');
          setShowAddModal(true);
          sessionStorage.removeItem('vance_new_event_project_id');
          sessionStorage.removeItem('vance_new_event_project_name');
        }
      })
      .catch(() => setIsLoading(false));
  }, [activeWorkspace, token]);

  const filteredEvents = searchQuery.trim()
    ? events.filter((e) =>
        e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (e.location || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : events;

  async function handleAdded(newEvent) {
    try {
      const res = await axios.get(`${API_BASE}/api/events`, {
        params: { workspace_id: activeWorkspace._id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data);
      const found = res.data.find((e) => e._id === newEvent._id);
      if (found) {
        setSelectedEvent(found);
        addRecentEvent(found);
      }
    } catch {
      setEvents((prev) => [newEvent, ...prev]);
      setSelectedEvent(newEvent);
    }
  }

  function handleEventUpdate(updatedEvent) {
    setEvents((prev) =>
      prev.map((e) => (e._id === updatedEvent._id ? updatedEvent : e))
    );
    setSelectedEvent(updatedEvent);
  }

  if (!activeWorkspace) {
    return (
      <div
        style={{
          backgroundColor: '#222222',
          minHeight: 'calc(100vh - 56px)',
          marginTop: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#565656' }}>
          Select a workspace to view events
        </span>
      </div>
    );
  }

  // ─── STATE B: Event Detail View ───
  if (selectedEvent) {
    return (
      <div
        style={{
          backgroundColor: '#222222',
          height: 'calc(100vh - 56px)',
          marginTop: '56px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '16px 48px 0 48px', flexShrink: 0 }}>
          <BackButton onClick={() => setSelectedEvent(null)} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <EventDetail
            key={selectedEvent._id}
            event={selectedEvent}
            onUpdate={handleEventUpdate}
            workspaceId={activeWorkspace._id}
          />
        </div>
      </div>
    );
  }

  // ─── STATE A: Event List View ───
  return (
    <div
      style={{
        backgroundColor: '#222222',
        minHeight: 'calc(100vh - 56px)',
        marginTop: '56px',
        padding: '32px',
      }}
    >
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '24px', color: '#FFFFFF', marginBottom: '4px' }}>
            Events
          </div>
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#888888' }}>
            Select an event to view details
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <SearchOutlined style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#565656', pointerEvents: 'none' }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events..."
            style={{
              width: '280px',
              backgroundColor: '#1a1a1a',
              border: '1px solid #3a3a3a',
              borderRadius: '6px',
              color: '#F5F5F5',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '13px',
              padding: '8px 10px 8px 34px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '80px', fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#565656' }}>
          Loading...
        </div>
      ) : filteredEvents.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '80px', gap: '12px' }}>
          <EventOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>
            {searchQuery ? 'No events match your search' : 'No events yet'}
          </div>
          {!searchQuery && (
            <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#3a3a3a', fontStyle: 'italic' }}>
              Events are created within Projects
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {filteredEvents.map((event) => (
            <EventCard
              key={event._id}
              event={event}
              onClick={() => {
                setSelectedEvent(event);
                addRecentEvent(event);
              }}
            />
          ))}
        </div>
      )}

      <AddEventModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddModalProjectId(null);
          setAddModalProjectName('');
        }}
        onSuccess={(e) => {
          setShowAddModal(false);
          setAddModalProjectId(null);
          setAddModalProjectName('');
          handleAdded(e);
        }}
        projectId={addModalProjectId}
        projectName={addModalProjectName}
      />
    </div>
  );
}
